import { patch } from "@iewcasia/Services/ApiService";
import isApiError from "@insite/client-framework/Common/isApiError";
import {
    API_URL_CURRENT_FRAGMENT,
    doesNotHaveExpand,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { AddProductApiParameter, Cart, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { BillToModel, CartLineModel, CartModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

export interface CustomAddProductApiParameter extends AddProductApiParameter {
    properties?: { [key: string]: string };
}

const cartsUrl = "/api/v1/carts";

export async function addProductWithResult(
    parameter: CustomAddProductApiParameter,
): Promise<ServiceResult<CartLineModel>> {
    const cartLine = {
        productId: parameter.productId,
        qtyOrdered: parameter.qtyOrdered,
        unitOfMeasure: parameter.unitOfMeasure,
        notes: parameter.notes,
        sectionOptions: parameter.sectionOptions,
    } as CartLineModel;

    cartLine.properties = {};

    if (parameter.subscription) {
        cartLine.properties.productSubscription = JSON.stringify(parameter.subscription);
    }

    if (parameter.properties) {
        cartLine.properties = { ...cartLine.properties, ...parameter.properties };
    }

    try {
        const cartLineModel = await post(`${cartsUrl}/${API_URL_CURRENT_FRAGMENT}/cartlines`, cartLine);
        return {
            successful: true,
            result: cartLineModel,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error.status === 403) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
                statusCode: 403,
            };
        }
        throw error;
    }
}

export async function updateCart(parameter: UpdateCartApiParameter) {
    const { cart } = parameter;
    const { billToId, shipToId } = cart;
    const patchModel: CartModel = {
        ...cart,
        billTo: billToId ? ({ id: billToId } as BillToModel) : null,
        shipTo: shipToId ? ({ id: shipToId } as ShipToModel) : null,
    };

    const cartModel = await patch<CartModel>(
        `${cartsUrl}/${patchModel.id}`,
        patchModel,
        parameter.additionalQueryStringParameters,
    );
    return cleanCartForUpdate(cartModel, { expand: ["paymentOptions"] });
}

function cleanCartForUpdate(cartModel: CartModel, parameter?: { expand?: string[]; additionalExpands?: string[] }) {
    cartModel.orderDate = cartModel.orderDate! && new Date(cartModel.orderDate!);
    cartModel.requestedPickupDateDisplay =
        cartModel.requestedPickupDateDisplay! && new Date(cartModel.requestedPickupDateDisplay!);
    cartModel.requestedDeliveryDateDisplay =
        cartModel.requestedDeliveryDateDisplay! && new Date(cartModel.requestedDeliveryDateDisplay!);

    if (doesNotHaveExpand(parameter, "cartLines")) {
        delete cartModel.cartLines;
    }
    if (doesNotHaveExpand(parameter, "paymentOptions")) {
        delete cartModel.paymentMethod;
        delete cartModel.paymentOptions;
    }
    if (doesNotHaveExpand(parameter, "carriers")) {
        delete cartModel.carriers;
    }
    if (doesNotHaveExpand(parameter, "validation")) {
        delete cartModel.billTo?.validation;
        if (cartModel.shipTo) {
            cartModel.shipTo.validation = null;
        }
    }
    const cartResult = {
        cart: cartModel as Cart,
        billTo: cartModel.billTo ? cartModel.billTo : undefined,
        shipTo: cartModel.shipTo ? cartModel.shipTo : undefined,
    };

    if (cartModel.billTo) {
        cartResult.cart.billToId = cartModel.billTo.id;
        delete (cartResult.cart as CartModel).billTo;
    }

    if (cartModel.shipTo) {
        cartResult.cart.shipToId = cartModel.shipTo.id;
        delete (cartResult.cart as CartModel).shipTo;
    }

    return cartResult;
}
